import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Microservices, ELaunchDarklyFlag } from '../constants/pol.enums';
import { UserPreferencesResponse } from '../models/global.model';
import { IMyOaasResponse, IUser } from '../models/state.model';
import { LaunchDarklyService } from '../layout/launch-darkly/launch-darkly.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  launchDarkly = inject(LaunchDarklyService);
  signUpFlag: boolean = false;
  signInFlag = false;

  constructor(private http: HttpClient) {
    this.launchDarkly.flags$.subscribe(res => {
      this.signUpFlag = res[ELaunchDarklyFlag.SignUp];
      this.signInFlag = res[ELaunchDarklyFlag.SignIn];
    });
  }

  getUserPreferences() {
    const apiUrl = `${Microservices.ProviderOnline}/UserPreferences`;
    return this.http.get<UserPreferencesResponse<IUser>>(apiUrl).pipe(
      map((response: UserPreferencesResponse<IUser>) => {
        return response?.userPreferences;
      })
    );
  }

  updateUserPreferences(user: IUser) {
    let apiUrl: string;
    
    if(this.signInFlag) {
      // if flag is enabled use V2
      apiUrl = `${Microservices.ProviderOnline}/v2/UserPreferences/Demographics`;
    } else {
      // if flag is disabled use V1
      apiUrl = `${Microservices.ProviderOnline}/UserPreferences/Demographics`;
    }

    return this.http.put<UserPreferencesResponse<IUser>>(apiUrl, user).pipe(
      map((response: UserPreferencesResponse<IUser>) => {
        return response?.userPreferences;
      })
    );
  }

  getMyOaas(): Observable<IMyOaasResponse> {
    const apiUrl = `${Microservices.SecurityGroup}/Users/MyOaas`;
    return this.http.get<IMyOaasResponse>(apiUrl).pipe(
      map(res => {
        // flatten the securityGroups vcodes array
        for(const group of res.securityGroups) {
          const taxIds: typeof group.taxIDs = [];

          for(const taxId of group.taxIDs) {
            for(const vcode of taxId.vCodes) {
              taxIds.push({ taxId: taxId.taxId, vCodes: [vcode], viewMore: taxId.viewMore });
            }
          }
          
          group.taxIDs = taxIds;
        }

        // flatten the oaaUsers vcodes array
        for(const user of res.oaaUsers) {
          const group = res.securityGroups.find(g => g.id === user.securityGroupId);

          if(group) {
            const taxIds: typeof group.taxIDs = [];

            for(const taxId of group.taxIDs) {
              for(const vcode of taxId.vCodes) {
                taxIds.push({ taxId: taxId.taxId, vCodes: [vcode], viewMore: taxId.viewMore });
              }
            }

            user.taxIDs = taxIds;
          }
        }

        return res;
      }),
      map(res => {
        // sort security groups ascending
        res.securityGroups.sort((a, b) => {
          if(a.name < b.name) {
            return -1;
          } else if(a.name > b.name) {
            return 1;
          }

          return 0;
        });

        for(const group of res.securityGroups) {
          // sort security group permissions ascending
          const result = group.permissions.filter(value=> value !=='Basic').sort();
          
          if(group.permissions.includes('Basic')) {
            result.splice(0, 0, 'Basic');
          }

          group.permissions = result;
          
          // sort security group taxIDs ascending.
          // this also sorts oaaUsers taxIDs because we're using
          // securityGroups as a key for oaaUsers
          group.taxIDs.sort((a, b) => {
            if(a.vCodes[0].name < b.vCodes[0].name) {
              return -1;
            } else if(a.vCodes[0].name > b.vCodes[0].name) {
              return 1;
            }
  
            return 0;
          });

          let taxIdCount = 0;
          group.taxIDs.map(i => taxIdCount += i.vCodes.length);
          group.viewMore = false
          group.taxIdCount = taxIdCount;
        }

        return res;
      }),
      map(res => {
        for(const user of res.oaaUsers) {
          const group = res.securityGroups.find(g => g.id === user.securityGroupId);

          if(group) {
            // get count of tax ids for the view more/less button
            let taxIdCount = 0;
            group.taxIDs.map(i => taxIdCount += i.vCodes.length);
            group.taxIDs.forEach(i => i.viewMore = false);
            group.taxIdCount = taxIdCount;

            // update the original object
            user.securityGroupName = group.name;
            user.taxIDs = group.taxIDs;
            user.taxIdCount = group.taxIdCount;

            const result = [];
            const columnCount = 5;

            // limit permission columns to `columnCount` rows
            for(let i = 0; i < group.permissions.length; i += columnCount) {
              result[result.length] = group.permissions.slice(i, i + columnCount);
            }

            user.permissions = result;
          } else {
            user.firstName = '';
            user.lastName = '';
            user.email = 'Inactive OAA';
          }
        }

        return res;
      })
    );
  }
}
